import MirrorBackButton from "@/components/MirrorBackButton";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import MobileLayout from "@/components/MobileLayout";
import PreviewNotice from "@/components/PreviewNotice";
import { Star, ArrowRight, Watch, ChevronLeft, Zap } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { useLanguage } from "@/i18n/LanguageProvider";

// ── Static data ────────────────────────────────────────────────────────────

// Labels resolved at render time via t()
const ENERGY_LABEL_KEYS = [
  "energy.level.exhausted",
  "energy.level.low",
  "energy.level.moderate",
  "energy.level.high",
  "energy.level.peak",
];

const ENERGY_CURVE: { hour: string; level: number }[] = [
  { hour: "08h", level: 2 },
  { hour: "10h", level: 5 },
  { hour: "12h", level: 4 },
  { hour: "14h", level: 2 },
  { hour: "16h", level: 3 },
  { hour: "18h", level: 4 },
];

const WEEK_DAYS = [
  { labelKey: "common.days.sun", level: 3 },
  { labelKey: "common.days.mon", level: 4 },
  { labelKey: "common.days.tue", level: 5 },
  { labelKey: "common.days.wed", level: 3 },
  { labelKey: "common.days.thu", level: 4 },
  { labelKey: "common.days.fri", level: 2 },
  { labelKey: "common.days.sat", level: 4, isToday: true },
];

const LEVEL_COLOR: Record<number, string> = {
  1: "hsl(0 60% 45%)",
  2: "hsl(218 14% 45%)",
  3: "hsl(220 55% 45%)",
  4: "hsl(187 55% 40%)",
  5: "#00e5ff",
};

const LEVEL_LABEL: Record<number, { labelKey: string; bg: string; text: string; border: string }> = {
  1: { labelKey: "energy.level.exhausted", bg: "hsl(0 55% 35% / 0.20)",   text: "hsl(0 60% 60%)",    border: "hsl(0 55% 40% / 0.30)"   },
  2: { labelKey: "energy.level.low",       bg: "hsl(220 9% 46% / 0.15)",  text: "hsl(218 14% 65%)",  border: "hsl(220 9% 46% / 0.25)"  },
  3: { labelKey: "energy.level.moderate",  bg: "hsl(220 55% 38% / 0.20)", text: "hsl(220 60% 65%)",  border: "hsl(220 55% 45% / 0.30)" },
  4: { labelKey: "energy.level.high",      bg: "hsl(187 55% 35% / 0.18)", text: "hsl(187 72% 60%)",  border: "hsl(187 60% 45% / 0.28)" },
  5: { labelKey: "energy.level.peak",      bg: "hsl(187 72% 50% / 0.14)", text: "#00e5ff",            border: "hsl(187 72% 50% / 0.40)" },
};

// ── Page ───────────────────────────────────────────────────────────────────

const Energy: React.FC = () => {
  const navigate  = useNavigate();
  const { toast } = useToast();
  const { t } = useLanguage();
  const [current, setCurrent] = useState(3);

  const stagger = (i: number) => ({ delay: 0.06 + i * 0.08, duration: 0.5 });
  const badge = LEVEL_LABEL[current];

  return (
    <MobileLayout className="pb-28 pt-8 relative overflow-hidden">
      <PreviewNotice />
      {/* Ambient glow */}
      <div className="absolute inset-0 pointer-events-none">
        <div
          className="absolute top-0 left-1/2 -translate-x-1/2 w-[380px] h-[220px] rounded-full opacity-[0.04]"
          style={{ background: "radial-gradient(ellipse 55% 50% at 50% 50%, hsl(187 72% 50%), transparent)" }}
        />
      </div>

      {/* BACK BUTTON */}
      <MirrorBackButton className="mb-4" />

      {/* HEADER */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="flex items-center justify-between mb-5"
      >
        <div>
          <p className="text-[9px] font-display tracking-[0.2em] uppercase mb-0.5"
            style={{ color: "hsl(187 72% 50% / 0.55)" }}>
            {t("energy.subtitle")}
          </p>
          <h1 className="font-display text-[26px] font-semibold text-foreground leading-none">
            {t("energy.title")}
          </h1>
        </div>
        <div className="w-9 h-9 rounded-xl flex items-center justify-center"
          style={{ background: "hsl(187 72% 50% / 0.10)", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
          <Zap size={15} style={{ color: "hsl(187 72% 50%)" }} />
        </div>
      </motion.div>

      {/* RIGHT NOW */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(1)}
        className="rounded-2xl p-5 mb-4 relative overflow-hidden"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.22)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
        <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-4"
          style={{ color: "hsl(187 72% 50% / 0.50)" }}>{t("energy.rightNow")}</p>

        {/* 5 circles */}
        <div className="flex items-center gap-2.5 mb-3">
          {[1, 2, 3, 4, 5].map((n) => (
            <div
              key={n}
              className="w-8 h-8 rounded-full transition-all duration-300"
              style={{
                background: n <= current ? LEVEL_COLOR[current] : "hsl(218 14% 20%)",
                border: `1.5px solid ${n <= current ? LEVEL_COLOR[current] : "hsl(218 14% 28%)"}`,
                boxShadow: n <= current && current >= 4 ? `0 0 8px ${LEVEL_COLOR[current]}55` : "none",
              }}
            />
          ))}
          <span
            className="ml-2 px-2.5 py-1 rounded-full font-display text-[10px] font-semibold"
            style={{ background: badge.bg, color: badge.text, border: `1px solid ${badge.border}` }}
          >
            {t(badge.labelKey)}
          </span>
        </div>

        <p className="text-[12px] font-body leading-relaxed mb-4" style={{ color: "hsl(218 14% 60%)" }}>
          {current === 1 && t("energy.desc.exhausted")}
          {current === 2 && t("energy.desc.low")}
          {current === 3 && t("energy.desc.moderate")}
          {current === 4 && t("energy.desc.high")}
          {current === 5 && t("energy.desc.peak")}
        </p>

        <button
          onClick={() => toast({ description: t("ux.preview.unavailable"), duration: 2000 })}
          className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide"
          style={{ color: "hsl(187 72% 55%)" }}
        >
          <span>{t("energy.updateNow")}</span>
          <ArrowRight size={11} />
        </button>
      </motion.div>

      {/* ENERGY CURVE */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(2)}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <p className="text-[10px] font-display tracking-[0.16em] uppercase mb-4"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("energy.energyCurve")}</p>

        <div className="flex items-end gap-1.5 h-[60px] mb-2">
          {ENERGY_CURVE.map((col, i) => {
            const isPeak = col.level === 5;
            const heightPct = (col.level / 5) * 100;
            return (
              <div key={col.hour} className="flex-1 flex flex-col items-center gap-1">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ height: `${heightPct}%` }}
                  transition={{ delay: 0.3 + i * 0.06, duration: 0.5, ease: "easeOut" }}
                  className="w-full rounded-t-sm self-end"
                  style={{
                    background: isPeak ? "#00e5ff" : col.level >= 3 ? "hsl(220 55% 38%)" : "hsl(218 14% 30%)",
                    minHeight: 4,
                  }}
                />
              </div>
            );
          })}
        </div>

        <div className="flex gap-1.5 mb-3">
          {ENERGY_CURVE.map((col) => (
            <p key={col.hour} className="flex-1 text-center text-[9px] font-display"
              style={{ color: "hsl(218 14% 40%)" }}>
              {col.hour}
            </p>
          ))}
        </div>

        <div className="flex items-center gap-4">
          {[
            { labelKey: "energy.legend.peak",  color: "hsl(var(--primary))" },
            { labelKey: "energy.legend.good",  color: "hsl(220 55% 38%)" },
            { labelKey: "energy.legend.low",   color: "hsl(218 14% 30%)" },
          ].map((l) => (
            <div key={l.labelKey} className="flex items-center gap-1.5">
              <div className="w-2.5 h-2.5 rounded-sm" style={{ background: l.color }} />
              <span className="text-[10px] font-body" style={{ color: "hsl(218 14% 50%)" }}>{t(l.labelKey)}</span>
            </div>
          ))}
        </div>
      </motion.div>

      {/* MIRROR RECOMMENDATION */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(3)}
        className="rounded-2xl p-4 mb-4 relative overflow-hidden"
        style={{ background: "hsl(187 72% 50% / 0.05)", border: "1px solid hsl(187 72% 50% / 0.22)" }}
      >
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-primary/25 to-transparent" />
        <div className="flex items-start gap-3">
          <div className="w-8 h-8 rounded-xl flex items-center justify-center shrink-0 mt-0.5"
            style={{ background: "hsl(187 72% 50% / 0.12)", border: "1px solid hsl(187 72% 50% / 0.20)" }}>
            <Star size={14} style={{ color: "hsl(187 72% 55%)" }} />
          </div>
          <div className="flex-1">
            <p className="text-[9px] font-display tracking-[0.18em] uppercase mb-1"
              style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("energy.mirrorRecommendation")}</p>
            <p className="text-[12px] font-body text-foreground/65 leading-relaxed mb-3">
              {t("energy.mirrorRecommendationText")}
            </p>
            <button
              onClick={() => navigate("/mirror")}
              className="flex items-center gap-1 font-display text-[11px] font-medium tracking-wide opacity-75 hover:opacity-100 transition-opacity"
              style={{ color: "hsl(187 72% 55%)" }}
            >
              <span>{t("energy.planWithMirror")}</span>
            </button>
          </div>
        </div>
      </motion.div>

      {/* LOG ENTRY */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(4)}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <p className="text-[10px] font-display tracking-[0.16em] uppercase mb-3"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("energy.logEntry")}</p>
        <div className="flex gap-2">
          {ENERGY_LABEL_KEYS.map((key, i) => {
            const lvl = i + 1;
            const isActive = current === lvl;
            return (
              <button
                key={lvl}
                onClick={() => {
                  setCurrent(lvl);
                  toast({ description: t("ux.preview.session.saved"), duration: 2000 });
                }}
                className="flex-1 flex flex-col items-center gap-1 py-2.5 rounded-xl font-display text-[9px] transition-all duration-200 active:scale-[0.96]"
                style={
                  isActive
                    ? { background: LEVEL_COLOR[lvl], color: "hsl(var(--background))", fontWeight: 700 }
                    : { background: "hsl(187 72% 50% / 0.07)", border: "1px solid hsl(187 72% 50% / 0.15)", color: "hsl(218 14% 55%)" }
                }
              >
                <span className="font-bold text-[14px]">{lvl}</span>
                <span className="text-[8px] tracking-wide leading-none text-center">{t(key)}</span>
              </button>
            );
          })}
        </div>
      </motion.div>

      {/* THIS WEEK */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(5)}
        className="rounded-2xl p-4 mb-4"
        style={{ background: "hsl(var(--card))", border: "1px solid hsl(187 72% 50% / 0.16)" }}
      >
        <p className="text-[10px] font-display tracking-[0.16em] uppercase mb-3"
          style={{ color: "hsl(187 72% 50% / 0.55)" }}>{t("energy.thisWeek")}</p>
        <div className="flex gap-2">
          {WEEK_DAYS.map((d) => {
            const lbl = LEVEL_LABEL[d.level];
            return (
              <div key={d.labelKey} className="flex-1 flex flex-col items-center gap-1.5">
                <div
                  className="w-full py-1.5 rounded-lg text-center font-display text-[10px] font-bold"
                  style={{
                    background: lbl.bg,
                    color: lbl.text,
                    border: `1px solid ${lbl.border}`,
                  }}
                >
                  {d.level}
                </div>
                <p className="text-[9px] font-display"
                  style={{ color: d.isToday ? "#00e5ff" : "hsl(218 14% 40%)" }}>
                  {t(d.labelKey)}
                </p>
              </div>
            );
          })}
        </div>
      </motion.div>

      {/* CONNECT WEARABLE */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={stagger(6)}
        className="rounded-2xl p-4 mb-5 flex items-center gap-3"
        style={{ background: "hsl(218 14% 12% / 0.6)", border: "1px solid hsl(var(--border) / 0.4)" }}
      >
        <div className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: "hsl(187 72% 50% / 0.08)", border: "1px solid hsl(187 72% 50% / 0.16)" }}>
          <Watch size={16} style={{ color: "hsl(187 72% 50% / 0.55)" }} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[12px] font-body leading-snug mb-2" style={{ color: "hsl(218 14% 55%)" }}>
            {t("energy.connectWearable")}
          </p>
          <button disabled
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-display text-[11px] font-medium tracking-wide opacity-35 cursor-not-allowed"
            style={{ border: "1px solid hsl(187 72% 50% / 0.30)", color: "hsl(187 72% 50%)" }}>
            <Watch size={12} />
            {t("energy.connectWearableBtn")}
          </button>
          <p className="text-[10px] font-display tracking-wide mt-1" style={{ color: "hsl(218 14% 35%)" }}>
            {t("energy.comingSoon")}
          </p>
        </div>
      </motion.div>

    </MobileLayout>
  );
};

export default Energy;
